/**
 * The flashcard's four ratings and the "next review" echo under them.
 *
 * Always rendered so it reserves its height, invisible and inert until the
 * card is revealed - the card stays put pre- and post-reveal. Two across, four
 * from `sm`; on a phone it is the runner's pinned footer, so four across and
 * 56px tall, without the keyboard digits (there is no keyboard).
 */

import {
  RecentRatingFeedback,
  defaultIntervalLabel,
} from '@/components/features/mindspace/practice/widgets/RecentRatingFeedback';
import { formatInterval } from '@/components/features/mindspace/practice/fsrsFormat';

type Intervals = { '1': number; '2': number; '3': number; '4': number };

interface RatingStyle {
  grade: 1 | 2 | 3 | 4;
  label: string;
  /** Static fallback hint used when the concept payload has no
   *  `predicted_intervals` (older deployments, mock data, etc.). */
  fallbackHint: string;
  cls: string;
}

// All four buttons are visually neutral so none reads as pre-selected before
// the user chooses. Label + interval + grade number differentiate them; the
// last choice is echoed via RecentRatingFeedback after rating.
const RATING_STYLES: RatingStyle[] = [
  {
    grade: 1,
    label: 'Again',
    fallbackHint: '<1m',
    cls: 'border-border text-foreground hover:bg-muted hover:border-gray-300 dark:hover:bg-white/[0.04]',
  },
  {
    grade: 2,
    label: 'Hard',
    fallbackHint: '6m',
    cls: 'border-border text-foreground hover:bg-muted hover:border-gray-300 dark:hover:bg-white/[0.04]',
  },
  {
    grade: 3,
    label: 'Good',
    fallbackHint: '1d',
    // Neutral like the other three - no button is pre-highlighted, so "Good"
    // doesn't read as already-selected before the user chooses.
    cls: 'border-border text-foreground hover:bg-muted hover:border-gray-300 dark:hover:bg-white/[0.04]',
  },
  {
    grade: 4,
    label: 'Easy',
    fallbackHint: '4d',
    cls: 'border-border text-foreground hover:bg-muted hover:border-gray-300 dark:hover:bg-white/[0.04]',
  },
];

/**
 * Per-card rating hint resolver. When the concept payload carries
 * live FSRS predictions (`predicted_intervals`), format each grade's
 * predicted next-review distance via `formatInterval(days)`. Otherwise
 * fall back to the static hint so older deployments keep rendering
 * meaningful copy on the rate buttons.
 */
function hintFor(style: RatingStyle, intervals: Intervals | undefined): string {
  if (!intervals) return style.fallbackHint;
  const days = intervals[String(style.grade) as '1' | '2' | '3' | '4'];
  if (!Number.isFinite(days)) return style.fallbackHint;
  return formatInterval(days);
}

export function FlashcardRatings({
  revealed,
  onRate,
  intervals,
  last,
}: {
  revealed: boolean;
  onRate: (grade: 1 | 2 | 3 | 4) => void;
  intervals: Intervals | undefined;
  /** The rating just given, echoed under the buttons while revealed. */
  last: { key: number; grade: 1 | 2 | 3 | 4 } | null;
}) {
  return (
    <div
      className={`flex flex-col gap-3 motion-safe:transition-opacity motion-safe:duration-200 ${
        revealed ? 'opacity-100' : 'pointer-events-none opacity-0'
      }`}
      aria-hidden={!revealed}
    >
      <div className='grid grid-cols-2 gap-2 max-md:grid-cols-4 sm:grid-cols-4'>
        {RATING_STYLES.map(r => (
          <button
            key={r.grade}
            type='button'
            onClick={() => onRate(r.grade)}
            disabled={!revealed}
            tabIndex={revealed ? 0 : -1}
            className={`flex h-12 flex-col items-center justify-center rounded-lg border bg-card transition-colors disabled:cursor-not-allowed disabled:opacity-40 max-md:h-14 max-md:rounded-xl ${r.cls}`}
          >
            <span className='flex items-baseline gap-1.5 text-sm font-medium'>
              {r.label}
              <span className='font-mono text-xs opacity-60 max-md:hidden'>
                {r.grade}
              </span>
            </span>
            <span className='text-xs opacity-60'>{hintFor(r, intervals)}</span>
          </button>
        ))}
      </div>

      <div className='flex h-6 items-center justify-center'>
        {revealed && last && (
          <RecentRatingFeedback
            rateKey={last.key}
            grade={last.grade}
            nextInterval={defaultIntervalLabel(last.grade)}
          />
        )}
      </div>
    </div>
  );
}
